/**
 * 「每一条录入 = 一行」之后最要紧的三件事，**纯函数、前后端共用**：
 *
 *   1. 这次粘贴里**每个位置**是哪一行的第几个字（`第 3 行第 2 个字`）——
 *      保存前的报错与预览里的提示都要说得出这个位置；
 *   2. **重复出现的字必须标音**（同一轮粘了两次，或者库里已经录过、这次又出现
 *      且是新的位置）；
 *   3. **同一个字不能标两次同样的音**（同一轮或跨轮都不行）。
 *
 * ## 用户原话（2026-10-19 的验收标准）
 *
 * > "如果是重复出现的、而我没有标音，那你保存的时候就不要通过，你要提示
 * >  '有个字没有标音'，标了音之后才能通过。如果有两个字我标了同样的音
 * >  （同一轮或者不同轮录入的字，出现同一个字标了同一个音），那你要提示
 * >  '这个音已经存在了，就不能再录入了'。检查没问题的，你才要保存。"
 *
 * ## 为什么放在 shared
 *
 * 服务层（提交前最后一道闸）与界面（点了「确认导入」之前先拦）必须用**同一套**
 * 判定：界面上自己数一遍、服务层再数一遍，两边的消息迟早对不上，而"为什么
 * 不能保存"正是用户唯一要看的那些字。
 *
 * ## 口径（三句话）
 *
 *   · **一行 = 一次录入的一个位置**（`hanzi_listing` 一行，`sort_order` 是位置）；
 *   · **只出现一次的字不用标音**（用默认读音 `hanzi.pinyin`）；
 *   · **重复出现的字** = 这次粘了 ≥2 次，**或者**库里已经有这个字、而这次这个
 *     位置的读音在库里没有（= 它是一条**新的**位置）。
 *
 * ⚠️ 已存在、读音也相同的位置是**幂等重贴**：既不用标音、也不算"重复录入"
 *    （第二次贴同一张表时，它应该显示"已存在、跳过"，而不是报错）。
 */

import { isHanziListType, type HanziListType } from './listing.js';

// ---------------------------------------------------------------- 类型

/**
 * 一个位置的读音"待确认"是**哪种原因**（2026-10-21，B 方案）。
 *
 *   · `mark`                —— 文件里的拼音末尾标了 `?`（生成文件的人没把握）；
 *   · `dictionary-mismatch` —— 文件里写的拼音 ≠ 字典拼音（用户 OCR 的声调不可信）；
 *   · `mark-and-mismatch`   —— 两者同时成立。
 *
 * ⚠️ 三种都要**拦保存**（`uncertain === true`），区别只在给用户看的那句话。
 */
export type ListingUncertainReason = 'mark' | 'dictionary-mismatch' | 'mark-and-mismatch';

/** 一次录入（解析后的一个位置）—— 服务层按解析顺序喂进来。 */
export interface ListingReadingOccurrence {
  /** 这个位置的汉字。 */
  charText: string;
  /** 原文行号（1 开始）。 */
  line: number;
  /** **这一行的第几个字**（1 开始，只数汉字；`天 地 人` → 天=1、地=2、人=3）。 */
  indexInLine: number;
  /**
   * 这次**显式标定**的读音；空串 = 没标（用默认读音）。
   *
   * ⚠️ 它与"默认读音"分开：没标音不等于"读默认音"这件事已经确认过 ——
   *    重复出现的字必须显式标，否则保存被拒。
   */
  reading: string;
  /** 这个字的默认读音（内置字表，`autoPinyin()`）。 */
  defaultReading: string;
  /**
   * 文件里这个位置标了「?」（不确定）、而且用户**还没在预览里确认**。
   * 省略 = `false`（老调用方 / 老文件没有这个标记）。
   *
   * ⚠️ 与 `reading` 各说一件事：`reading` 是"这个位置现在用什么音"，
   *    `uncertain` 是"这个音**还没经过人确认**"。带 `?` 的位置即使写了读音，
   *    也要等用户确认才能保存（见下面的「第三道」）。
   */
  uncertain?: boolean;
  /**
   * 文件里这个位置标了「?」（用户确认之后 `uncertain` 会变成 false，
   * 但这个标记**留着** —— 预览里要靠它把确认过的卡片画成绿色）。
   *
   * 省略 = 跟随 `uncertain`（只传 `uncertain` 的调用方照旧能工作）。
   */
  wasUncertain?: boolean;
  /**
   * 这个位置为什么"待确认"（2026-10-21，B 方案）：
   *
   *   · `mark`                     —— 文件里的拼音末尾标了 `?`；
   *   · `dictionary-mismatch`      —— **文件里写的拼音 ≠ 字典拼音**；
   *   · `mark-and-mismatch`        —— 两者同时成立。
   *
   * 省略 = `mark`（老调用方 / 老数据只有 `?` 这一种来源）。
   * ⚠️ 它只影响**文案**：不管哪种来源，`uncertain === true` ⇒ 不许保存。
   */
  uncertainReason?: ListingUncertainReason;
  /** 文件里这个位置**写的拼音**（归一后；空串 / 省略 = 文件没写）。 */
  fileReading?: string;
  /**
   * 字典拼音（`availableReadings(char)[0]`；空串 / 省略 = 内置表没收录这个字）。
   *
   * ⚠️ 它只是**核对的参照**，不是"这个位置该读什么"：单音字落库用它
   *    （见解析器 `fillAutoReadingForChar()`），多音字仍用文件写的那个。
   */
  dictionaryReading?: string;
  /** 作用域：同一本书 + 同一册 + 同一张表（不同作用域互不影响）。 */
  scope: string;
  /** 表类型（`shizi` / `xiezi`）。 */
  listType: HanziListType;
}

/** 库里已有的一条收录（只取校验用得着的列）。 */
export interface ExistingListingReading {
  /** 与服务层喂进来的 `scope` 同口径。 */
  scope: string;
  charText: string;
  /** **生效读音**：`reading ?? hanzi.pinyin`（老数据 `reading` 为空时用默认音）。 */
  reading: string;
}

/** 预览里字卡/提示要用的一个位置。 */
export interface ListingPositionInfo {
  charText: string;
  line: number;
  indexInLine: number;
  /** 人话位置：`第 3 行第 2 个字`。 */
  label: string;
  /**
   * **这个位置落在哪张字表上**（`shizi` / `xiezi`）。
   *
   * 预览按"粘贴时的行"分组时要用它把两张表分开：同一次粘贴里可以既有
   * 「识字表」的行、又有「写字表」的行（表名行会切换后面行的表类型），
   * 那时分组标题要写成 `识字表 · 第 3 行（7 个字）` —— 两个同为「第 3 行」的
   * 分组才不会混在一起。
   *
   * `null` = 老后端没给这个字段（那时标题只说行号，不编一个表名出来）。
   */
  listType: HanziListType | null;

  /** 显式标定的读音（空串 = 没标）。 */
  reading: string;
  /** 生效读音（显式，或默认读音）。 */
  effectiveReading: string;
  /** 这个字**在这次粘贴里**出现了几次（同作用域）。 */
  occurrenceCount: number;
  /** true = 预览里要用**重复底色**标出来（这次重复，或库里已有这个字）。 */
  duplicate: boolean;
  /** true = 这个位置在库里已经有同字同音的一条（重复导入会跳过它）。 */
  existing: boolean;
  /** true = 这个位置**必须标音**（重复出现 / 是新位置），没标就会被拒。 */
  needsReading: boolean;
  /**
   * 文件里在这个位置的拼音末尾标了「?」（不确定）、而且用户**还没确认**。
   *
   * 预览里这张卡标**浅橙**底色；`ok === false` 时**不许保存**（第三道校验）。
   * 老后端 / 老数据没有这个字段 ⇒ 省略（当 `false`）。
   */
  uncertain?: boolean;
  /**
   * 文件里在这个位置标过「?」（不论现在确认了没有）。
   *
   * 用途只有一个：**确认之后卡片要变绿**（`uncertain=false && wasUncertain=true`）。
   * 少了它，一个只出现一次的字确认完会退回白底 —— 用户就看不出"我确认过了"。
   *
   * ⚠️ 2026-10-21 起它也是"这个位置**曾经**待确认过"（来源可以是 `?`，也可以是
   *    「文件拼音 ≠ 字典拼音」）—— 两种来源确认之后都要变绿。
   */
  wasUncertain?: boolean;
  /** 待确认的**原因**（省略 = `mark`；只在 `uncertain` / `wasUncertain` 时有意义）。 */
  uncertainReason?: ListingUncertainReason;
  /** 文件里这个位置写的拼音（归一后；空串 / 省略 = 文件没写）。 */
  fileReading?: string;
  /** 字典拼音（`availableReadings(char)[0]`；空串 / 省略 = 内置表没收录）。 */
  dictionaryReading?: string;
}

/** 一条"保存前校验"的问题。 */
export type ListingReadingIssueKind =
  | 'missing-reading'
  | 'duplicate-reading'
  /**
   * 这个位置的读音要用户确认（2026-10-20 新增，2026-10-21 扩了来源）：
   *   · 文件里标了 `?`；**或**
   *   · 文件里写的拼音 ≠ 字典拼音（B 方案）。
   */
  | 'needs-confirmation';

export interface ListingReadingIssue {
  kind: ListingReadingIssueKind;
  charText: string;
  /** 出问题的位置。 */
  line: number;
  indexInLine: number;
  label: string;
  /** 重复音里的那个读音（`missing-reading` 时是空串）。 */
  reading: string;
  /** 同字同音的**另一处**位置（人话；没有就为空串）。 */
  alsoLabel: string;
  /** 给人看的一句话（后端 4xx 的 details / 界面提示共用）。 */
  message: string;
}

export interface ListingReadingCheck {
  /** 三道校验全过 = true（false 时**不许保存**）。 */
  ok: boolean;
  /** 逐位置信息（预览字卡 + 位置提示用）。 */
  positions: ListingPositionInfo[];
  /** 没标音的位置。 */
  missing: ListingReadingIssue[];
  /** 同字同音的位置。 */
  conflicts: ListingReadingIssue[];
  /**
   * **读音要用户确认**的位置（第三道，2026-10-20；2026-10-21 扩了来源）。
   *
   * 两种来源（可同时成立，文案不同、拦截相同）：
   *   1. 文件里的拼音末尾标了 `?`；
   *   2. **文件里写的拼音 ≠ 字典拼音**（B 方案："那些'和字典不一致'的依然要用底色呈现"）。
   *
   * 用户原话（验收标准）：
   * > "你识别时，对你不确定的读音做个标识……系统识别这个标识，在预览里用底色标出
   * >  这些字；我点那个字，弹窗里看那个音对不对……点完确认之后，底色要改变。"
   * > "带 `?` 且未确认的位置 → 等同『没标音』→ 不许保存。"
   *
   * `ok === false` 时「确认导入」禁用、后端提交返回 4xx。
   */
  needsConfirm: ListingReadingIssue[];
  /** 没标音的**字**（去重，按第一次出现顺序）—— 标题里那句"有 N 个字没有标音"。 */
  missingChars: string[];
  /** `missingChars` 的人话汇总（`有 2 个字没有标音：长（第 3 行第 2 个字）、…`）。 */
  missingSummary: string;
  /** 待确认的**字**（去重，按第一次出现顺序）—— 标题里那句"有 N 个字的读音需要你确认"。 */
  needsConfirmChars: string[];
  /** `needsConfirmChars` 的人话汇总（`有 2 个字的读音需要你确认：…，确认之后才能保存。`）。 */
  needsConfirmSummary: string;
}

// ---------------------------------------------------------------- 小工具

/** 人话位置。 */
export function listingPositionLabel(line: number, indexInLine: number): string {
  return `第 ${line} 行第 ${indexInLine} 个字`;
}

/** 作用域键（服务层与界面用同一份；不进库）。 */
export function listingReadingScopeKey(parts: {
  textbookId: string;
  gradeCode: string;
  semester: string;
  listType: string;
}): string {
  return `${parts.textbookId}|${parts.gradeCode}|${parts.semester}|${parts.listType}`;
}

/** 同字同音的键。 */
function readingKey(scope: string, charText: string, reading: string): string {
  return `${scope}\u0000${charText}\u0000${reading}`;
}

/** 同作用域里同一个字（不分读音）的键。 */
function charKey(scope: string, charText: string): string {
  return `${scope}\u0000${charText}`;
}

/**
 * **「文件里写的拼音 ≠ 字典拼音」那句提示**（2026-10-21，B 方案）。
 *
 * 用户点名的样子：
 * > 「空」这个位置，文件里写的是 `kōng`，字典默认是 `kòng` —— 请对着课本确认是哪个。
 *
 * 这里补上「第几行第几个字」（用户要求**逐字给**），并在**同时**标了 `?` 时
 * 追加一句，说清两个原因都成立（但仍然是同一个位置、同一个确认动作）。
 */
function describeDictionaryMismatch(
  position: Pick<ListingPositionInfo, 'charText' | 'label' | 'fileReading' | 'dictionaryReading'>,
  alsoMarked: boolean,
): string {
  const file = position.fileReading !== undefined && position.fileReading !== '' ? position.fileReading : '（空）';
  const dictionary =
    position.dictionaryReading !== undefined && position.dictionaryReading !== ''
      ? position.dictionaryReading
      : '（字典里没有）';
  const head =
    `「${position.charText}」（${position.label}）这个位置，文件里写的是「${file}」，` +
    `字典默认是「${dictionary}」—— 请对着课本确认是哪个。`;
  return alsoMarked ? `${head}（文件里还标了「?」，也不确定。）` : head;
}

// ---------------------------------------------------------------- 校验

/**
 * 三道校验（**唯一的判定口径**）：
 *
 *   1. 重复出现的字没标音 → `missing`（每处一条）；
 *   2. 同一个字同一个音出现两次（同一轮或跨轮）→ `conflicts`；
 *   3. 文件里标了 `?`、用户还没确认 → `needsConfirm`（2026-10-20）。
 *   全过才 `ok: true`。
 *
 * ⚠️ 第 3 道与第 1 道**互斥地**报同一处：带 `?` 又没读音的位置只在
 *    `needsConfirm` 里出现一次（否则同一个位置会弹两条红字）。
 *
 * @param occurrences 这次粘贴的**每一个位置**（顺序 = 录入顺序；含会被合并的那些，
 *                    因为"第 3 行第 2 个字"可能正是被合并掉的那一处）。
 * @param existing    库里**同一批作用域**里已有的收录（生效读音）。
 */
export function checkListingReadings(
  occurrences: readonly ListingReadingOccurrence[],
  existing: readonly ExistingListingReading[] = [],
): ListingReadingCheck {
  /** 库里每个(字,音)有几条（正常只有 0/1；2 条说明历史数据，按 2 条算）。 */
  const existingByReading = new Map<string, number>();
  const existingByChar = new Map<string, number>();
  for (const row of existing) {
    if (row.charText === '' || row.reading === '') continue;
    existingByReading.set(
      readingKey(row.scope, row.charText, row.reading),
      (existingByReading.get(readingKey(row.scope, row.charText, row.reading)) ?? 0) + 1,
    );
    existingByChar.set(
      charKey(row.scope, row.charText),
      (existingByChar.get(charKey(row.scope, row.charText)) ?? 0) + 1,
    );
  }

  /**
   * 这次粘贴里同一个字出现在几个位置（同作用域）。
   *
   * ⚠️ 同一个字出现在**两张表**上（识字表 + 写字表）不是"重复出现"：
   *    作用域里带了表类型，两个作用域的键不同。
   */
  const occurrencesByChar = new Map<string, ListingReadingOccurrence[]>();
  for (const occurrence of occurrences) {
    const key = charKey(occurrence.scope, occurrence.charText);
    const bucket = occurrencesByChar.get(key) ?? [];
    bucket.push(occurrence);
    occurrencesByChar.set(key, bucket);
  }

  /** 每个(字,音)在这次粘贴里有几处（只数显式标了音的）。 */
  const markedByReading = new Map<string, ListingReadingOccurrence[]>();
  for (const occurrence of occurrences) {
    const reading = occurrence.reading.trim();
    if (reading === '') continue;
    const key = readingKey(occurrence.scope, occurrence.charText, reading);
    const bucket = markedByReading.get(key) ?? [];
    bucket.push(occurrence);
    markedByReading.set(key, bucket);
  }

  const missing: ListingReadingIssue[] = [];
  const conflicts: ListingReadingIssue[] = [];
  const needsConfirm: ListingReadingIssue[] = [];
  const positions: ListingPositionInfo[] = [];
  const missingChars: string[] = [];
  const needsConfirmChars: string[] = [];
  /** 每个位置是否是"这次要新建的一条"（用于判断冲突落在哪一处）。 */
  const conflicting = new Set<ListingReadingOccurrence>();

  for (const occurrence of occurrences) {
    const scopeCharKey = charKey(occurrence.scope, occurrence.charText);
    const sameChar = occurrencesByChar.get(scopeCharKey) ?? [];
    const explicit = occurrence.reading.trim();
    const effective = explicit !== '' ? explicit : occurrence.defaultReading;
    const sameReadingExisting = existingByReading.get(
      readingKey(occurrence.scope, occurrence.charText, effective),
    ) ?? 0;
    const existingCharCount = existingByChar.get(scopeCharKey) ?? 0;
    const repeatedInPaste = sameChar.length > 1;
    const uncertain = occurrence.uncertain === true;
    /** 文件里标过 `?`：确认之后 `uncertain` 会变 false，但这个标记要留着（画绿卡）。 */
    const wasUncertain = occurrence.wasUncertain === true || uncertain;

    /**
     * **需要标音**的两种情形：
     *   · 这个字在这次粘贴里出现了不止一次（用户说的"重复出现的"）；
     *   · 库里已经有这个字，但这个位置的读音在库里没有（= 新增一个位置）。
     *
     * ⚠️ 已存在、读音也相同的**幂等重贴**不需要标音：它会被跳过，不会新增行。
     * ⚠️ 带 `?` 且**没写读音**的位置也在这里算"需要标音"（`needsReading` = true）：
     *    浮窗里"还出现在哪"要按它显示"未标音"。但**第一道不会重复报它** ——
     *    它由下面的「第三道」单独说（同一件事别弹两条红字）。
     */
    const needsReading =
      explicit === '' && (repeatedInPaste || (existingCharCount > 0 && sameReadingExisting === 0));

    const existing = sameReadingExisting > 0;
    const duplicate = repeatedInPaste || existingCharCount > 0;

    positions.push({
      charText: occurrence.charText,
      line: occurrence.line,
      indexInLine: occurrence.indexInLine,
      label: listingPositionLabel(occurrence.line, occurrence.indexInLine),
      listType: occurrence.listType,
      reading: explicit,
      effectiveReading: effective,
      occurrenceCount: sameChar.length,
      duplicate,
      existing,
      needsReading,
      uncertain,
      wasUncertain,
      /**
       * 待确认的**原因**（2026-10-21）：省略 = `mark`（老调用方只有 `?`）。
       * 只有"文件拼音 ≠ 字典拼音"时才会是另外两种 —— 判定在解析器里。
       */
      ...(occurrence.uncertainReason !== undefined
        ? { uncertainReason: occurrence.uncertainReason }
        : {}),
      ...(occurrence.fileReading !== undefined && occurrence.fileReading !== ''
        ? { fileReading: occurrence.fileReading }
        : {}),
      ...(occurrence.dictionaryReading !== undefined && occurrence.dictionaryReading !== ''
        ? { dictionaryReading: occurrence.dictionaryReading }
        : {}),
    });
  }

  /** ---- 第一道：重复出现的字没标音（带 `?` 的交给第三道，不重复报） ---- */
  for (const position of positions) {
    if (!position.needsReading || position.uncertain === true) continue;
    const message =
      `「${position.charText}」（${position.label}）重复出现了，但没有标音 —— ` +
      '请给它选一个读音，标了音才能保存。';
    missing.push({
      kind: 'missing-reading',
      charText: position.charText,
      line: position.line,
      indexInLine: position.indexInLine,
      label: position.label,
      reading: '',
      alsoLabel: '',
      message,
    });
    if (!missingChars.includes(position.charText)) missingChars.push(position.charText);
  }

  /**
   * ---- 第三道（2026-10-20，2026-10-21 扩了来源）：读音要用户确认 ----
   *
   * 两种来源（可同时成立，**都拦保存**）：
   *
   *   1. 文件里的拼音末尾标了 `?` —— 生成文件的人没把握；
   *   2. **文件里写的拼音 ≠ 字典拼音**（B 方案，2026-10-21）——
   *      用户的原话："有些没有标识多音字的字，它的声调你没有识别正确"、
   *      "用 B 比较好，核对几十个，好过全部核一遍。那些'和字典不一致'的依然要用底色呈现"。
   *
   * 文案必须**逐字说清是哪一种**（用户要求"逐字给"）：
   *   · `?` → 既有那句「在文件里标了「?」（不确定）—— 点它确认这个音」；
   *   · 不一致 → 「文件里写的是 kōng，字典默认是 kòng —— 请对着课本确认是哪个」。
   *
   * ⚠️ 判定只看 `uncertain`，**不看有没有读音**：文件给了 `dé?` 也算"没确认"，
   *    因为那个音正是生成文件的人没把握的那个。
   * ⚠️ 用户确认（点浮窗里的读音、或点「确认就是「dé」」）之后，服务层会把这个
   *    位置标成已确认 ⇒ `uncertain` 变 false ⇒ 这道校验自然通过。
   */
  for (const position of positions) {
    if (position.uncertain !== true) continue;
    const reason: ListingUncertainReason = position.uncertainReason ?? 'mark';
    const byDictionary = reason === 'dictionary-mismatch' || reason === 'mark-and-mismatch';
    const message = byDictionary
      ? describeDictionaryMismatch(position, reason === 'mark-and-mismatch')
      : position.reading === ''
        ? `「${position.charText}」（${position.label}）在文件里标了「?」、但没写读音 —— ` +
          '点它选一个读音并确认，才能保存。'
        : `「${position.charText}」（${position.label}）的读音「${position.reading}」在文件里标了「?」（不确定）—— ` +
          '点它确认这个音，才能保存。';
    needsConfirm.push({
      kind: 'needs-confirmation',
      charText: position.charText,
      line: position.line,
      indexInLine: position.indexInLine,
      label: position.label,
      reading: position.reading,
      alsoLabel: '',
      message,
    });
    if (!needsConfirmChars.includes(position.charText))
      needsConfirmChars.push(position.charText);
  }

  /** ---- 第二道：同一个字标了同样的音（同一轮或跨轮） ---- */
  for (const [key, group] of markedByReading) {
    const [first] = group;
    if (first === undefined) continue;
    const reading = first.reading.trim();
    const existingCount = existingByReading.get(key) ?? 0;
    /**
     * 允许的数量 = `max(1, 库里已有的条数)`：
     *   · 库里 0 条 → 这一轮最多 1 处（2 处 = 同轮重复）；
     *   · 库里 1 条 → 这一轮再来 1 处是幂等重贴（跳过），第 2 处才是重复录入；
     *   · 库里 2 条（历史脏数据）→ 允许 2 处。
     */
    const allowed = Math.max(1, existingCount);
    if (group.length <= allowed) continue;

    for (const [index, occurrence] of group.entries()) {
      if (index < allowed) continue;
      conflicting.add(occurrence);
      const label = listingPositionLabel(occurrence.line, occurrence.indexInLine);
      /** 同一条消息里给"另一处"：优先同一轮里的第一处，否则说库里已有。 */
      const other =
        group.length > 1 && group[0] !== occurrence
          ? listingPositionLabel(group[0]!.line, group[0]!.indexInLine)
          : '';
      const message =
        existingCount > 0
          ? `『${occurrence.charText} · ${reading}』这个音已经录过了（${label} 与库里已有的「${occurrence.charText} · ${reading}」），不能重复录入。`
          : `『${occurrence.charText} · ${reading}』这个音重复了（${label} 和 ${other} 都标了 ${reading}）—— 同一个字同一个音只能录一次。`;
      conflicts.push({
        kind: 'duplicate-reading',
        charText: occurrence.charText,
        line: occurrence.line,
        indexInLine: occurrence.indexInLine,
        label,
        reading,
        alsoLabel: other,
        message,
      });
    }
  }

  const missingSummary =
    missingChars.length === 0
      ? ''
      : `有 ${missingChars.length} 个字没有标音：${missingChars
          .map((char) => {
            const at = positions.find((position) => position.charText === char && position.needsReading);
            return `${char}（${at?.label ?? '位置没拿到'}）`;
          })
          .join('、')}`;

  /**
   * 用户点名的提示文案：**「有 N 个字的读音需要你确认」** + **逐字给出**位置
   * （`得（第 3 行第 1 个字）、背（第 7 行第 2 个字）`）。
   */
  const needsConfirmSummary =
    needsConfirmChars.length === 0
      ? ''
      : `有 ${needsConfirmChars.length} 个字的读音需要你确认：${needsConfirmChars
          .map((char) => {
            const at = positions.find(
              (position) => position.charText === char && position.uncertain === true,
            );
            return `${char}（${at?.label ?? '位置没拿到'}）`;
          })
          .join('、')}，确认之后才能保存。`;

  return {
    ok: missing.length === 0 && conflicts.length === 0 && needsConfirm.length === 0,
    positions,
    missing,
    conflicts,
    needsConfirm,
    missingChars,
    missingSummary: missingSummary === '' ? '' : `${missingSummary}，标了音才能保存。`,
    needsConfirmChars,
    needsConfirmSummary,
  };
}

// ---------------------------------------------------------------- 渲染成人话

/** 一个重复出现的字，其它位置在哪（预览里那行"位置提示"）。 */
export interface RepeatedPositionHint {
  charText: string;
  /** 第一次出现的位置。 */
  first: ListingPositionInfo;
  /** 其它位置（按行号/字序）。 */
  others: ListingPositionInfo[];
  /** 人话：`第 3 行第 2 个字「长」重复了（还出现在第 7 行第 1 个字）`。 */
  message: string;
}

/**
 * **提示重复的位置**（用户要靠它排查是不是录错）：
 * 每个重复出现的字给一句「第 3 行第 2 个字『长』重复了（还出现在第 7 行第 1 个字）」。
 *
 * 只列这次粘贴里出现 ≥2 次的字；`existing`（库里已有）的那些**不在这里** ——
 * 它们不是"这份文本里录重了"，而是"之前已经录过"，界面上另有那一行统计。
 */
export function describeRepeatedPositions(
  positions: readonly ListingPositionInfo[],
): RepeatedPositionHint[] {
  const buckets = new Map<string, ListingPositionInfo[]>();
  for (const position of positions) {
    const bucket = buckets.get(position.charText) ?? [];
    bucket.push(position);
    buckets.set(position.charText, bucket);
  }
  const out: RepeatedPositionHint[] = [];
  for (const [charText, bucket] of buckets) {
    if (bucket.length < 2) continue;
    const [first, ...others] = bucket;
    if (first === undefined) continue;
    out.push({
      charText,
      first,
      others,
      message:
        `${first.label}「${charText}」重复了（还出现在 ${others
          .map((item) => item.label)
          .join('、')}）`,
    });
  }
  return out;
}

/**
 * 预览字卡上那句话（这个位置现在是什么状态）。
 *
 * ⚠️ 刻意**不显示拼音**（用户原话："拼音你先不标在导入结果那里"）——
 *    这句话只说"位置 + 有没有标音"，读音由选音面板负责。
 */
export function describePositionReading(position: ListingPositionInfo): string {
  if (position.reading !== '') return `已标音：${position.reading}`;
  if (position.needsReading) return '还没有标音（点它选读音）';
  if (position.existing) return '之前录过（同字同音，会跳过）';
  return '只出现一次，用默认读音';
}

/** 选音面板里那句说明。 */
export function describeReadingChoice(charText: string, readings: readonly string[]): string {
  return readings.length === 0
    ? `内置字表里没有「${charText}」的读音 —— 请先到「浏览与编辑」里给它填一个读音`
    : `给「${charText}」的这个位置选一个读音（可选：${readings.join(' / ')}）`;
}

/** 表类型是不是字表（把词语表排除在这三道校验之外）。 */
export function isHanziListingScope(listType: string): listType is HanziListType {
  return isHanziListType(listType);
}
